import { Tier } from "../../models/tiers";
import { config } from "../config";
import { currencyFormatter } from "../currency";
import i18n from "../translations/i18n";

const { t } = i18n;

/**
 * Parses userSelections array to object
 * @param userSelections in array format
 * @returns userSelections in object format
 */
export const convertArrayToObject = (inputArray: any) => inputArray.reduce((obj, item) => {
    const [key, value] = item.split(':');
    obj[key.trim()] = value.trim();
    return obj;
}, {});


/**
 * Gets the multiValStr.
 * @param productSpec
 * @returns multiValueStr
 */
export const getMultiValueString = (productSpec: any): string => {
    const multiValueAttribute = productSpec.selectableAttributes.find(
        (attribute) => attribute.multiDisplayValue !== undefined
    );

    if (!multiValueAttribute) return '';

    const { multiDisplayValue, attributeKey, attributeValues = [] } = multiValueAttribute;
    const multiValueAttrName:string = attributeKey.toLowerCase();

    let multiValStr:string = `${config('MULTI_AP')}=${encodeURIComponent(`${multiValueAttrName}+${multiValueAttrName}=`)}`;

    const newMultiDisplayValue = multiDisplayValue.map(val => {
        const [key, value] = val.split("|");
        if (key && value) {
            const matchingEntry = attributeValues.find(attr => attr?.value === key);
            const matchingAttributeKey = matchingEntry ? matchingEntry.originalValue : key;
            return `${matchingAttributeKey}|${value}`;
        }
        return val;
    });

    newMultiDisplayValue.forEach((item, idx) => {
        multiValStr += encodeURIComponent(item);
        if (idx < newMultiDisplayValue.length - 1) { // Add `~` if not the last item
            multiValStr += "~";
        }
    });

    return multiValStr;
}

export const sortLineItems = (lineItems, order) => {
    if(order.length > 0){
        return lineItems.sort((itemA, itemB) => {
            const valueA = itemA.data.multiSelectData.value;
            const valueB = itemB.data.multiSelectData.value;
            return order.indexOf(valueA) - order.indexOf(valueB);
          });
    }
    return lineItems;
  };


  export const calculateThresholdStatus = (cart, discountThreshold) => {
    const threshold = discountThreshold;
    const cartSubTotalCent = cart?.cartSubtotal;
    const fractionDigit = cart?.fractionDigits || 0;
    const cartSubTotal = fractionDigit ? (cartSubTotalCent / Math.pow(10, fractionDigit)).toFixed(fractionDigit) : cartSubTotalCent;
    // const cartSubTotal = cart?.customCartSubTotal; we check which field do we need to use here as ctools will match based on custom field
    
    const isThresholdMet = typeof threshold === 'number' ? (cartSubTotal >= threshold) : false;
    const remainingAmountCent = Math.max((threshold - cartSubTotal) * Math.pow(10, fractionDigit), 0);;
    const percentage = (threshold > 0 ? (cartSubTotal / threshold) * 100 : 0);

    return {
        threshold,
        cartSubTotal,
        isThresholdMet,
        remainingAmountCent,
        percentage,
    };
}

export const getThresholdFromValidatedCoupon= (data) => {
    if (!data?.thresholdValue) return null;

    const { couponThreshold, discountThreshold } = data.thresholdValue;

    if (couponThreshold !== null && discountThreshold !== null) {
        return Math.max(couponThreshold, discountThreshold);
    }

    // Return whichever is defined, if any
    return couponThreshold !== null ? couponThreshold : discountThreshold;
}

export const filterAttributes = (attributes) => {
    return Object.entries(attributes || {})
      .filter(([key]) => key !== 'Quantity' && key !== 'Size' && key !== 'Product Size')
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
  };

export const filterOutQuantityAndSize = (arr) => arr.filter(item => 
    !item.includes("Quantity") && !item.includes("Size") && !item.includes("Product Size") 
);

export const isShippingCoupon = (data) => {
    return (dispatch, getState) => {
      try {
        const { applicationState: { user } } = getState();
        const regex = /\bshipping\b/i;
  
        if (!Array.isArray(data.cartDiscounts)) {
          return false;
        }
  
        // Iterate over cartDiscounts array and check for "shipping"
        for (const item of data.cartDiscounts) {
          if (regex.test(item.target)) {
            return true;
          }
        }
        return false;
      } catch (error) {
        console.log(error);
        return false; // In case of an error, return false as default
      }
    };
  };

  export const hasFullDiscount = (data) => {
    return (dispatch, getState) => {
      try {
        const { applicationState: { user } } = getState();
  
        if (!Array.isArray(data.cartDiscounts)) {
          return false;
        }
  
        // Iterate over cartDiscounts array and check permyriad in targetValue
        for (const item of data.cartDiscounts) {
          if (item.targetValue && item.targetValue.permyriad === 10000) {
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error(error);
        return false; // In case of an error, return false as default
      }
    };
  };
/**
     * Generate tier price data using pricing api response.
     * @param priceObj pricing api response
     * @param taxValue lineItem tax rate
     * @returns tier price object
     */
export const getTierPricesData = (priceObj: any, taxValue: any) => {
    const newPriceObj: any = { tierObject: [], oldPrices: [], isStrikeOutPricing: !!priceObj?.isStrikeOutPricing };
    const divideBy: number = priceObj?.fractionDigits ? Math.pow(10, priceObj.fractionDigits) : 1;

    const calculateIncTaxSubTotal = (totalExclTax: number) =>
        totalExclTax && taxValue !== null && taxValue !== undefined && !isNaN(taxValue)
            ? (Math.round(totalExclTax + totalExclTax * taxValue) / divideBy).toFixed(2)
            : 0;

    const formatPriceData = (tierItem: any, quantity: number, currencyCode: string): Tier => {
        const unitPriceWithoutCurrency = tierItem?.unitPrice ? tierItem.unitPrice / divideBy : 0;
        const unitPrice = currencyFormatter(unitPriceWithoutCurrency);
        const subtotalValue = tierItem?.totalExclTax ? tierItem.totalExclTax / divideBy : 0;
        const incTaxValue = calculateIncTaxSubTotal(tierItem?.totalExclTax || 0);

        return {
            quantity: quantity ? quantity.toString() : "",
            freeQuantity: tierItem?.freeQuantity ? tierItem.freeQuantity : 0,
            currencyCode: currencyCode || "",
            upsellQuantity: tierItem?.upsellQuantity ? tierItem.upsellQuantity.toString() : "",
            upsellUnitPrice: tierItem?.upsellUnitPrice ? tierItem.upsellUnitPrice.toString() : "",
            unitPriceWithoutCurrency: unitPriceWithoutCurrency.toString(),
            unitPrice,
            logoCharge: tierItem?.logoCharge,
            setupCharge: tierItem?.setupCharge,
            subtotal: currencyFormatter(tierItem.totalExclTax, priceObj.fractionDigits),
            subtotalValue: subtotalValue ? subtotalValue.toString() : "",
            incTaxValue: incTaxValue ? incTaxValue.toString() : "",
            incTaxValueWithCurrency: incTaxValue ? currencyFormatter(incTaxValue) : ""
        };
    };

    priceObj?.tiers?.forEach((item: any) => {
        const { tier, discountedTier } = item;
        const selectedTier = discountedTier || tier;

        if (selectedTier) {
            // Add tierObject entry for discountedTier
            newPriceObj.tierObject.push(
                formatPriceData(selectedTier, item.quantity, priceObj?.currencyCode)
            );
        }

        if (tier) {
            // Add oldPrices entry for tier
            newPriceObj.oldPrices.push(
                formatPriceData(tier, item.quantity, priceObj?.currencyCode)
            );
        }
    });

    return newPriceObj;
};

export function getLocalizedSizeValue(size, selectableAttributes) {
    const selectedAttribute = selectableAttributes?.find(attr => 
        attr.attributeKey === "Size" || attr.attributeKey === "Product Size"
    );
    const data = selectedAttribute?.attributeValues || [];
    const found = data.find(item => item.originalValue === size);
    return found?.value ? found.value : size;
}

export function hasLineItem(lineItemId, parsedArray) {
    const item = parsedArray.find(obj => obj.lineItemId === lineItemId);
    if (item && item.SIZE) {
        return item.SIZE.trim() !== "";
    }
}

export const backTocart = (e, navigate) => {
  e.preventDefault()
  const headerRoot : any = document.querySelector("[id=cart-header]");
  if(headerRoot) headerRoot.style.display = "block"
  const shippingHeader : any = document.querySelector("[id=shipping-header]");
  if(shippingHeader) shippingHeader.style.display = "none"
  navigate(`/${config('locale_details').current_sitename}/${t('shopping_cart_details.shoppingPageUrl')}`);
}